import { type WorkspaceResource } from '@/features/iam/api/resources/workspaces'

/**
 * Describes how the workspace was preselected.
 */
export type PreselectedWorkspace = PreselectedByUrl | PreselectedById

/**
 * The workspace was preselected by the URL the app is served from.
 */
export interface PreselectedByUrl {
  type: 'Url'
  /**
   * ID of the workspace that was preselected
   */
  workspaceId: string
  /**
   * The workspace resource as it was provided by the server.
   * May be stale.
   */
  workspaceResource: WorkspaceResource
}

/**
 * The workspace
 */
export interface PreselectedById {
  type: 'Id'
  /**
   * ID of the workspace that was preselected
   */
  workspaceId: string
}

/**
 * Provides a way to determine whether we have a pre-selected workspace,
 * either based on the environment (custom domain) or a stored setting.
 */
export interface PreselectedWorkspaceProvider {
  /**
   * Gets the preselected workspace if there is one.
   */
  read(): PreselectedWorkspace | null

  /**
   * Saves the given ID as the last selected workspace.
   *
   * @param workspaceId
   */
  save(workspaceId: string): void
}

const LAST_WORKSPACE_ID_KEY = 'lastWorkspaceId'

/**
 * Default implementation.
 */
export class DefaultPreselectedWorkspaceProvider implements PreselectedWorkspaceProvider {
  static readonly instance = new DefaultPreselectedWorkspaceProvider()

  read(): PreselectedWorkspace | null {
    if (window.__workspaceDomain) {
      return {
        type: 'Url',
        workspaceId: window.__workspaceDomain.workspace.id,
        workspaceResource: window.__workspaceDomain.workspace,
      }
    }

    const lastWorkspaceId = sessionStorage.getItem(LAST_WORKSPACE_ID_KEY)
    if (lastWorkspaceId) {
      return { type: 'Id', workspaceId: lastWorkspaceId }
    }

    return null
  }

  save(workspaceId: string) {
    // Don't write anything if we're already on a domain that serves
    // a preselected workspace.
    if (window.__workspaceDomain) {
      return
    }

    sessionStorage.setItem(LAST_WORKSPACE_ID_KEY, workspaceId)
  }
}
